import { AionCta } from "@/components/site/AionCta";
import { KeywordHubLinks } from "@/components/site/KeywordHubLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Mic, TrendingUp, Users, Quote, Play } from "lucide-react";
import defaultHeroWebp from "@/assets/hero-portrait.webp";
import heroWebp480 from "@/assets/hero-portrait-480.webp";
import heroWebp768 from "@/assets/hero-portrait-768.webp";

const heroSrcSet = `${heroWebp480} 480w, ${heroWebp768} 768w, ${defaultHeroWebp} 900w`;
const heroSizes = "(max-width: 768px) 100vw, 40vw";
import { episodes } from "@/lib/podcast-episodes";
const homeFaq = [
  {
    q: "Who is Raj Shamani?",
    a: "Raj Shamani is an Indian entrepreneur, angel investor, motivational speaker and the host of Figuring Out — one of India's most-watched long-form business podcasts. He is also the author of the bestselling book Build, Don't Talk.",
  },
  {
    q: "What is Raj Shamani's age?",
    a: "Raj Shamani was born on 22 March 1996 in Indore, Madhya Pradesh, India. As of 2026, he is 30 years old. He grew up in a Marwari business family and started working on the factory floor as a teenager.",
  },
  {
    q: "What is Raj Shamani's net worth?",
    a: "Raj Shamani has built wealth across manufacturing, venture building, and early-stage investing. He co-founded House of X, holds 20+ angel investments across D2C and fintech, and is associated with Shamani Industries. Exact net worth figures vary by source and are not publicly disclosed.",
  },
  {
    q: "What is Raj Shamani famous for?",
    a: "He is best known for hosting the Figuring Out podcast, for co-founding House of X (a venture studio backing Indian consumer brands), for his early-stage investments across D2C, fintech and the creator economy, and for his bestselling book Build, Don't Talk.",
  },
  {
    q: "Where is Raj Shamani from?",
    a: "Raj Shamani was born and raised in Indore, Madhya Pradesh, India, in a family that ran a manufacturing business. He started selling on the factory floor as a teenager before moving into content, investing and venture building.",
  },
  {
    q: "What is the Figuring Out podcast?",
    a: "Figuring Out is Raj Shamani's flagship podcast featuring long-form, unfiltered conversations with founders, investors, athletes, policymakers and outliers shaping India's next decade. Episodes are available on YouTube, Spotify and Apple Podcasts.",
  },
  {
    q: "What companies has Raj Shamani founded or invested in?",
    a: "Raj Shamani co-founded House of X, a venture studio backing Indian consumer brands, and is associated with Shamani Industries, his family's manufacturing business. As an angel investor he has 20+ active positions across direct-to-consumer, fintech and the creator economy.",
  },
  {
    q: "Did Raj Shamani write a book?",
    a: "Yes. Raj Shamani is the author of Build, Don't Talk — a bestselling, India-first playbook on money, communication and building leverage in your twenties and thirties.",
  },
  {
    q: "What is Raj Shamani's educational qualification?",
    a: "Raj Shamani studied at Daly College in Indore. He did not pursue an MBA — he learned business by working in his family's manufacturing company, Shamani Industries, and later by building his own media and venture businesses.",
  },
  {
    q: "What is House of X?",
    a: "House of X is the venture studio Raj Shamani co-founded to build and back Indian direct-to-consumer brands. It combines capital, distribution and brand-building support for early-stage consumer companies.",
  },
  {
    q: "How can I contact Raj Shamani for a brand collaboration or speaking event?",
    a: "Brand partnerships, speaking enquiries and media requests go through the official contact page on rajshamani.life, which lists the partnerships email used by his team.",
  },
  {
    q: "How can I be a guest on the Figuring Out podcast?",
    a: "Guest pitches are submitted through the Be a Guest page on rajshamani.life. The team reviews each pitch and selects guests based on the depth and originality of the story being shared.",
  },
  {
    q: "Where can I watch or listen to the Figuring Out podcast?",
    a: "Figuring Out episodes are published on YouTube in full video and are also available as audio on Spotify and Apple Podcasts. Every episode is indexed with a summary on the podcast page of this site.",
  },
  {
    q: "How does Raj Shamani earn his income?",
    a: "His income comes from five streams: the family manufacturing business Shamani Industries, the Figuring Out media business including YouTube and sponsorships, brand partnerships, paid speaking engagements, and equity in House of X along with 20+ angel investments.",
  },
  {
    q: "Is Raj Shamani married, and who is his wife?",
    a: "Raj Shamani has not publicly announced a marriage or partner. He keeps his personal life private, and most claims about a wife or girlfriend circulating online are unverified speculation rather than confirmed information.",
  },
  {
    q: "What is Raj Shamani's official Instagram account?",
    a: "Raj Shamani is active on Instagram, YouTube, LinkedIn and X through verified official accounts. Fake profiles using his name to promote investment tips or giveaways are common, so always check for the verified badge before engaging.",
  },
  {
    q: "How can I book Raj Shamani as a speaker?",
    a: "Raj Shamani speaks on building without permission, the creator economy, direct-to-consumer brands in India and AI leverage for founders. Speaking enquiries are handled by his team through the official partnerships email listed on the contact page.",
  },
  {
    q: "Who is Raj Shamani's father?",
    a: "Raj Shamani's father runs Shamani Industries, the family manufacturing business in Indore. Raj started working on the factory floor there as a teenager and credits the experience with teaching him sales, operations, and business judgement.",
  },
  {
    q: "What is Raj Shamani's net worth in rupees?",
    a: "No audited net worth figure is publicly available. His wealth is spread across Shamani Industries, House of X, the Figuring Out media business, brand partnerships, speaking fees, and 20+ angel investments. Online estimates vary widely and should be treated as unverified.",
  },
  {
    q: "Why is Raj Shamani famous?",
    a: "Raj Shamani is famous for hosting the Figuring Out podcast, one of India's most-watched long-form business shows. He is also known for his bestselling book Build, Don't Talk, his investments in Indian startups, and his keynote speeches on entrepreneurship and the creator economy.",
  },
  {
    q: "How did Raj Shamani become famous?",
    a: "He built his reputation by documenting his journey as a young entrepreneur in India, first through Shamani Industries and then by creating long-form content. Figuring Out grew through consistent, unfiltered interviews with founders, investors, and public figures.",
  },
  {
    q: "Where does Raj Shamani live?",
    a: "Raj Shamani was born and raised in Indore, Madhya Pradesh, and is currently based in Mumbai, Maharashtra, where he runs his media and venture businesses.",
  },
  {
    q: "Is Raj Shamani related to Kiara Advani?",
    a: "No. Raj Shamani and Kiara Advani are not related. She appeared as a guest on Figuring Out, which led to online speculation, but they have no family relationship.",
  },
  {
    q: "What religion is Raj Shamani?",
    a: "Raj Shamani is Hindu and comes from a Marwari business family based in Indore, Madhya Pradesh.",
  },
  {
    q: "What is Raj Shamani's caste?",
    a: "Raj Shamani has described his family as Marwari, a business community originally from the Marwar region of Rajasthan. He rarely discusses caste publicly and focuses on entrepreneurship and business instead.",
  },
  {
    q: "What is the latest Figuring Out podcast episode?",
    a: "New episodes of Figuring Out are released regularly on YouTube, Spotify and Apple Podcasts. The podcast page on this site lists every episode with a summary, key takeaways and a transcript snippet.",
  },
  {
    q: "Who is the host of the Figuring Out podcast?",
    a: "Figuring Out is hosted by Raj Shamani, an Indian entrepreneur and angel investor. He interviews founders, investors, athletes, actors and policymakers in long-form conversations about building businesses and careers in India.",
  },
  {
    q: "What is Raj Shamani's YouTube channel?",
    a: "Raj Shamani's YouTube channel hosts every full Figuring Out podcast episode in video, along with Shorts and clips. It is one of the most-watched business YouTube channels in India.",
  },
  {
    q: "Who has been a guest on the Figuring Out podcast?",
    a: "Figuring Out has featured guests including Bill Gates, Khan Sir, Kiara Advani, Janhvi Kapoor, Amit Kilhor and former Chief Justice DY Chandrachud — alongside founders, investors, cricketers and operators. The full guest list is indexed on this site with episode links.",
  },
  {
    q: "What does 'figuring out' mean in Raj Shamani's podcast?",
    a: "Figuring Out is the name of Raj Shamani's podcast — the idea that nobody has it all figured out, and every episode is a conversation about how guests worked things out as they built their careers and companies.",
  },
  {
    q: "What is Aion, and is it Raj Shamani's company?",
    a: "Aion is the AI engineering education venture associated with Raj Shamani's ecosystem, teaching practical AI skills to Indian builders and founders. The dedicated Aion page on this site explains the programme and links to aion.engineer.",
  },
  {
    q: "How old was Raj Shamani when he started his first business?",
    a: "Raj Shamani began working in his family's manufacturing business, Shamani Industries, as a teenager, and was selling and running operations well before his twenties. His success story page covers what he built at each age.",
  },
  {
    q: "What is Raj Shamani's biography or wiki summary?",
    a: "Raj Shamani is a 30-year-old Indian entrepreneur from Indore: he hosts the Figuring Out podcast, wrote Build, Don't Talk, co-founded House of X, is associated with Shamani Industries, and holds 20+ angel investments. The full wiki-style biography is on the biography page.",
  },
  {
    q: "What is the Build, Don't Talk book about?",
    a: "Build, Don't Talk by Raj Shamani is a practical playbook on money, communication and leverage for young Indians. It distils lessons from running a manufacturing business, building media properties and investing in startups into short, actionable chapters.",
  },
  {
    q: "What does the Raj Shamani podcast cover?",
    a: "Figuring Out covers entrepreneurship, startups, investing, personal finance, the creator economy and career-building in India. Guests range from Bollywood actors and cricketers to startup founders, judges and global business leaders.",
  },
  {
    q: "What is Raj Shamani's YouTube channel?",
    a: "Raj Shamani's official YouTube channel publishes full video episodes of Figuring Out along with clips and shorts on business, money and career advice. It is one of India's most-watched business channels.",
  },
  {
    q: "How can startups or brands work with Raj Shamani?",
    a: "Brands can reach his team for partnerships and sponsorships through the official contact page on rajshamani.life. Startups raising capital can pitch House of X, his venture studio for Indian consumer brands.",
  },
  {
    q: "Where can I find Raj Shamani quotes?",
    a: "The quotes page on this site collects Raj Shamani's most-shared lines on business, money and building, each with the context of the interview or speech it came from.",
  },
];



export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Raj Shamani — Official Site | Bio, Net Worth, Podcast", key: "title" },
      { name: "description", content: "The official Raj Shamani website. Verified bio, age (30), net worth sources, all Figuring Out podcast episodes, House of X and Build, Don't Talk.", key: "description" },
      { name: "author", content: "Raj Shamani", key: "author" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1", key: "robots" },
      { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1", key: "googlebot" },
      { name: "geo.region", content: "IN", key: "geo.region" },
      { name: "geo.placename", content: "Mumbai, India", key: "geo.placename" },
      { property: "og:title", content: "Raj Shamani — Official Site | Bio, Net Worth, Podcast", key: "og:title" },
      { property: "og:description", content: "The official Raj Shamani website. Verified bio, age (30), net worth sources, all Figuring Out podcast episodes, House of X and Build, Don't Talk.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/", key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { property: "og:image", content: "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg", key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani — entrepreneur, investor and host of the Figuring Out podcast", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "twitter:title", content: "Raj Shamani — Official Site | Bio, Net Worth, Podcast", key: "twitter:title" },
      { name: "twitter:description", content: "The official Raj Shamani website. Verified bio, age (30), net worth sources, all Figuring Out podcast episodes, House of X and Build, Don't Talk.", key: "twitter:description" },
      { name: "twitter:image", content: "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg", key: "twitter:image" },
    ],
    links: [
      { rel: "canonical", href: "https://rajshamani.life/" },
      {
        rel: "preload",
        as: "image",
        href: heroWebp480,
        imagesrcset: heroSrcSet,
        imagesizes: heroSizes,
        type: "image/webp",
        fetchpriority: "high",
      } as unknown as Record<string, string>,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": "https://rajshamani.life/#person",
              name: "Raj Shamani",
              alternateName: ["Rajkumar Shamani", "Raj Shamani Official"],
              url: "https://rajshamani.life/",
              image: {
                "@type": "ImageObject",
                url: "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg",
                width: 1200,
                height: 630,
                caption: "Raj Shamani — entrepreneur, investor and host of Figuring Out",
              },
              jobTitle: "Entrepreneur, Investor & Podcaster",
              description:
                "Raj Shamani is an Indian entrepreneur, angel investor and host of Figuring Out — one of India's most-watched long-form business podcasts.",
              gender: "Male",
              nationality: "Indian",
              birthPlace: { "@type": "Place", name: "Indore, Madhya Pradesh, India" },
              knowsAbout: [
                "Entrepreneurship",
                "Angel Investing",
                "Podcasting",
                "Personal Branding",
                "D2C Brands",
                "Creator Economy",
                "Indian Startups",
              ],
              sameAs: [
                "https://www.youtube.com/@rajshamani",
                "https://www.instagram.com/rajshamani",
                "https://www.linkedin.com/in/rajshamani",
                "https://twitter.com/rajshamani",
                "https://en.wikipedia.org/wiki/Raj_Shamani",
              ],
              worksFor: { "@id": "https://rajshamani.life/#organization" },
              owns: [
                { "@type": "Organization", name: "House of X" },
                { "@type": "Organization", name: "Shamani Industries" },
              ],
            },
            {
              "@type": "Book",
              "@id": "https://rajshamani.life/build-dont-talk#book",
              name: "Build, Don't Talk",
              url: "https://rajshamani.life/build-dont-talk",
              author: { "@id": "https://rajshamani.life/#person" },
              inLanguage: "en",
              numberOfPages: 224,
              isbn: "9789356290471",
              bookFormat: "https://schema.org/Paperback",
              datePublished: "2022-11-21",
              publisher: { "@type": "Organization", name: "HarperCollins India" },
              image:
                "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg",
              workExample: [
                {
                  "@type": "Book",
                  "@id": "https://rajshamani.life/build-dont-talk#paperback",
                  isbn: "9789356290471",
                  bookEdition: "1st Edition",
                  bookFormat: "https://schema.org/Paperback",
                  inLanguage: "en",
                  datePublished: "2022-11-21",
                  potentialAction: {
                    "@type": "ReadAction",
                    target: {
                      "@type": "EntryPoint",
                      urlTemplate: "https://rajshamani.life/build-dont-talk",
                      actionPlatform: [
                        "https://schema.org/DesktopWebPlatform",
                        "https://schema.org/MobileWebPlatform",
                      ],
                    },
                    expectsAcceptanceOf: {
                      "@type": "Offer",
                      category: "purchase",
                      eligibleRegion: { "@type": "Country", name: "IN" },
                    },
                  },
                },
              ],
            },


          ],
        }),
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "https://rajshamani.life/#webpage",
              name: "Raj Shamani — Official Site | Bio, Net Worth, Podcast",
              headline: "Raj Shamani: Age, Net Worth in Rupees, Biography, Wiki, Podcast & Book",
              description: "The official Raj Shamani website. Verified bio, age (30), net worth sources, all Figuring Out podcast episodes, House of X and Build, Don't Talk.",
          url: "https://rajshamani.life/",
          inLanguage: "en",
          isPartOf: { "@id": "https://rajshamani.life/#website" },
          about: { "@id": "https://rajshamani.life/#person" },
          primaryImageOfPage: { "@id": "https://rajshamani.life/#logo" },
          image: { "@id": "https://rajshamani.life/#logo" },
          mainEntity: { "@id": "https://rajshamani.life/#faq" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://rajshamani.life/#faq",
          mainEntity: homeFaq.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: {
              "@type": "Answer",
              text: a,
            },
          })),
          isPartOf: { "@id": "https://rajshamani.life/#website" },
          url: "https://rajshamani.life/",
        }),
      },

    ],
  }),

  component: Home,
});

function Home() {
  return (
    <div className="relative overflow-hidden">
      <div className="relative">

      {/* Hero — editorial, restrained */}
      <section className="relative border-b border-border bg-transparent">
        <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-muted-foreground mb-8">
              <span className="w-6 h-px bg-gold" />
              Entrepreneur · Investor · Podcaster
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-semibold leading-[1.02] tracking-tight">
              Raj Shamani —
              <br />
              <span className="text-gold">entrepreneur, investor</span>
              <br />
              and host of Figuring Out.
            </h1>
             <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
               <strong className="text-foreground">Raj Shamani</strong> is an Indian entrepreneur, angel investor,
               and host of <em>Figuring Out</em> — one of India's most-watched business podcasts.
               Explore his biography, ventures, investments, podcast episodes and bestselling book <em>Build, Don't Talk</em>.
             </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/podcast"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
              >
                Watch the Podcast
                <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
              </Link>
              <Link
                to="/guest"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border text-foreground hover:border-gold/60 hover:text-gold transition"
              >
                Request a Booking
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative overflow-hidden rounded-sm border border-border">
              <img
                src={defaultHeroWebp}
                srcSet={heroSrcSet}
                sizes={heroSizes}
                alt="Raj Shamani — entrepreneur, investor and host of Figuring Out"
                width={900}
                height={1200}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="aspect-[3/4] object-cover w-full grayscale-[15%] will-change-transform"
              />
              <div className="absolute -bottom-px left-0 right-0 h-px bg-gold/60" />
            </div>
          </div>
        </div>
      </section>

      {/* Keyword-focused section — above the fold */}
      <section className="border-b border-border bg-noir/40">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mb-4">
              Raj Shamani: Age, Net Worth in Rupees, Biography, Wiki, Podcast & Book
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Quick answers to the most searched questions about Raj Shamani: his age (born 22 March 1996), his net worth across ventures and investments, his biography, the <Link to="/podcast" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Figuring Out podcast</Link>, and the bestselling book <Link to="/build-dont-talk" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Build, Don't Talk</Link>.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <Link to="/about" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">
                Full biography <ArrowRight size={14} />
              </Link>
              <Link to="/faq" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">
                Raj Shamani FAQ <ArrowRight size={14} />
              </Link>
            </div>

            {/* Quick facts — answer table for entity queries */}
            <div className="mt-8 overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <caption className="sr-only">Raj Shamani quick facts — age, date of birth, birthplace, education, profession and net worth</caption>
                <tbody>
                {[
                  ["Full name", "Raj Shamani"],
                  ["Date of birth", "22 March 1996"],
                  ["Age (2026)", "30 years old"],
                  ["Birthplace", "Indore, Madhya Pradesh, India"],
                  ["Hometown", "Indore, Madhya Pradesh, India"],
                  ["Current base", "Mumbai, Maharashtra, India"],
                  ["Education", "Daly College, Indore"],
                  ["Profession", "Entrepreneur, angel investor, podcaster, author"],
                  ["Father", "Runs Shamani Industries, the family's manufacturing business"],
                  ["Family business", "Shamani Industries"],
                  ["Podcast", "Figuring Out with Raj Shamani"],
                  ["Book", "Build, Don't Talk (2022)"],
                  ["Ventures", "House of X, Shamani Industries"],
                  ["Nationality", "Indian"],
                  ["Religion", "Hindu"],
                  ["Languages", "English, Hindi"],
                ].map(([k, v]) => (
                    <tr key={k} className="border-b border-border/60 last:border-0">
                      <th scope="row" className="text-left font-medium text-muted-foreground px-4 py-2.5 w-1/2 md:w-1/3">
                        {k}
                      </th>
                      <td className="px-4 py-2.5 text-foreground">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Answer-first keyword grid — one short answer per high-volume query */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight mb-3">
            What people search about Raj Shamani
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-10">
            Short, verified answers to the most-searched Raj Shamani questions — income, education, family, wife, Instagram, YouTube, quotes, the Figuring Out podcast guests, Build, Don't Talk, his biography, lifestyle and Aion — each linking to the full page.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                q: "How does Raj Shamani earn money?",
                a: "Five streams: Shamani Industries manufacturing, the Figuring Out media business, brand partnerships, paid speaking, and equity in House of X plus 20+ angel investments.",
                to: "/raj-shamani-income",
                label: "Income breakdown",
              },
              {
                q: "What is Raj Shamani's qualification?",
                a: "He studied at Daly College, Indore and holds a bachelor's degree. He deliberately skipped an MBA and learned business inside his family's factory instead.",
                to: "/raj-shamani-education",
                label: "Education & qualification",
              },
              {
                q: "Who is in Raj Shamani's family?",
                a: "He was born into a Marwari business family in Indore. His father runs Shamani Industries, the manufacturing company where Raj first learned selling.",
                to: "/raj-shamani-family",
                label: "Family & background",
              },
              {
                q: "Is Raj Shamani married?",
                a: "No marriage has been publicly announced. He keeps his relationships private, and most 'Raj Shamani wife' results online are unverified speculation.",
                to: "/raj-shamani-wife",
                label: "Personal life",
              },
              {
                q: "What is Raj Shamani's official Instagram?",
                a: "His verified accounts are on Instagram, YouTube, LinkedIn and X. Impersonation and investment-tip accounts using his name are common — check the verified handles.",
                to: "/raj-shamani-instagram",
                label: "Official accounts",
              },
              {
                q: "How do I book Raj Shamani as a speaker?",
                a: "Keynotes cover building without permission, the creator economy, D2C in India and AI leverage for founders. Enquiries go through the official partnerships email.",
                to: "/raj-shamani-speaker",
                label: "Speaking & keynotes",
              },
              {
                q: "What is Raj Shamani's net worth in rupees?",
                a: "Net worth estimates vary by source. Most of his wealth comes from Shamani Industries, House of X, the Figuring Out media business, and 20+ angel investments. Exact figures are not publicly disclosed.",
                to: "/raj-shamani-net-worth",
                label: "Net worth breakdown",
              },
              {
                q: "What is Shamani Industries?",
                a: "Shamani Industries is the family manufacturing business in Indore where Raj Shamani started working on the factory floor as a teenager. It remains a core part of his business portfolio.",
                to: "/shamani-industries",
                label: "Shamani Industries",
              },
              {
                q: "What are Raj Shamani's most famous quotes?",
                a: "His best-known lines are about building instead of talking, choosing leverage over labour, and documenting your work in public. Read the full collection with context.",
                to: "/raj-shamani-quotes",
                label: "Raj Shamani quotes",
              },
              {
                q: "Who hosts the Figuring Out podcast?",
                a: "Raj Shamani hosts Figuring Out — long-form interviews with founders, investors, actors, cricketers and policymakers building India's next decade.",
                to: "/raj-shamani-podcast",
                label: "About the podcast",
              },
              {
                q: "What is the Build, Don't Talk book?",
                a: "Raj Shamani's bestselling playbook on money, communication and leverage for young Indians — read the chapter-by-chapter summary and where to buy it.",
                to: "/raj-shamani-books",
                label: "Book summary",
              },
              {
                q: "Where does Raj Shamani live, and what does he own?",
                a: "Born in Indore, now based in Mumbai. The lifestyle page separates verified facts about his house, cars and routine from unverified claims.",
                to: "/raj-shamani-lifestyle",
                label: "Lifestyle, house & cars",
              },
              {
                q: "Who are the biggest Figuring Out podcast guests?",
                a: "Bill Gates, Khan Sir, Kiara Advani, Janhvi Kapoor, Amit Kilhor and DY Chandrachud — plus founders, cricketers and investors. Browse the complete guest list with episode links.",
                to: "/figuring-out-podcast-guests",
                label: "Full guest list",
              },
              {
                q: "What is Raj Shamani's wiki-style biography?",
                a: "From Indore's factory floor to the UN stage — birthplace, schooling, first business, the podcast, the book and the investments, in one structured life story.",
                to: "/raj-shamani-biography",
                label: "Read the biography",
              },
              {
                q: "What is Aion, Raj Shamani's AI venture?",
                a: "Aion teaches practical AI engineering to Indian builders and founders. Learn what the programme covers, who it is for, and how to sign up.",
                to: "/aion",
                label: "About Aion",
              },
            ].map((item) => (
              <div key={item.to} className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold mb-2">{item.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{item.a}</p>
                <Link to={item.to} className="inline-flex items-center gap-1.5 text-sm text-gold hover:opacity-80">
                  {item.label} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Credibility strip */}
      <section className="border-b border-border bg-noir/40">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-6">
            Raj Shamani by the numbers
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
            {[
              { v: "17M+", l: "Audience Across Platforms" },
              { v: "500+", l: "Long-Form Episodes" },
              { v: "20+", l: "Portfolio Investments" },
              { v: "10+", l: "Years Building Brands" },
            ].map((s) => (
              <div key={s.l} className="border-l border-gold/40 pl-4">
                <div className="text-3xl md:text-4xl font-display font-semibold text-foreground tabular-nums">{s.v}</div>
                <div className="text-[11px] text-muted-foreground mt-2 uppercase tracking-[0.15em]">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* As featured in */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground text-center mb-6">
            Raj Shamani featured in
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-foreground">
            {["Forbes", "YourStory", "ET Now", "Mint", "CNBC-TV18", "Inc42"].map((n) => (
              <span key={n} className="text-base md:text-lg font-display tracking-wide">
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Practice areas */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Practice</div>
            <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight">
              Three disciplines. One thesis.
            </h2>
          </div>
          <p className="md:col-span-7 md:pt-12 text-muted-foreground text-lg leading-relaxed">
            Capital, content, and category-defining brands — applied to the
            opportunity of an India coming of age. Each practice informs the
            other; together they compound.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
          {[
            { icon: Mic, t: "Podcasting", d: "Host of Figuring Out — long-form conversations with founders, operators, and policymakers shaping India.", to: "/podcast", cta: "Explore the podcast" },
            { icon: TrendingUp, t: "Investing", d: "Early-stage capital across consumer, fintech, and the creator economy. Twenty-plus active positions.", to: "/ventures", cta: "View the portfolio" },
            { icon: Users, t: "Building", d: "Co-founder at multiple consumer ventures. Operating experience behind every cheque written.", to: "/ventures", cta: "See the ventures" },
          ].map((c) => (
            <Link
              key={c.t}
              to={c.to}
              className="group p-10 bg-background hover:bg-noir/40 transition"
            >
              <c.icon className="text-gold mb-8" size={24} strokeWidth={1.5} />
              <h3 className="text-xl font-display font-semibold mb-3">{c.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.d}</p>
              <div className="mt-8 text-[11px] text-gold uppercase tracking-[0.2em] flex items-center gap-2 group-hover:gap-3 transition-all">
                {c.cta} <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Pull quote */}
      <section className="border-y border-border bg-noir/30">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          <h2 className="sr-only">In Raj Shamani's words</h2>
          <Quote className="mx-auto text-gold mb-6" size={28} strokeWidth={1.5} />
          <blockquote className="text-2xl md:text-3xl font-display leading-snug text-foreground">
            "The best conversations don't perform — they reveal. That's the
            standard we hold every guest, and ourselves, to."
          </blockquote>
          <div className="mt-8 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            Raj Shamani · Host, Figuring Out
          </div>
        </div>
      </section>

      {/* About / Biography */}
      <section className="border-y border-border bg-noir/20">
        <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">About Raj</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
              An Indian entrepreneur figuring it out — in public.
            </h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-muted-foreground leading-relaxed">
            <p>
              Raj Shamani is an Indian entrepreneur, angel investor, and content
              creator best known as the host of <em>Figuring Out</em> — one of
              India's most-watched long-form business podcasts. Born in Indore
              and raised inside a family manufacturing business, he started
              selling on the factory floor before he could legally vote, and
              has spent the last decade translating that operator's instinct
              into brands, capital, and conversations.
            </p>
            <p>
              As a founder, Raj co-built House of X, a venture studio backing
              consumer brands targeting India's next 300 million spenders. As
              an investor, he writes early-stage cheques across direct-to-consumer,
              fintech, and the creator economy — more than twenty active
              positions and counting. As a speaker, he has shared stages with
              the United Nations, IIMs, IITs, and Fortune 500 leadership rooms
              across four continents.
            </p>
            <p>
              The through-line is the podcast. <em>Figuring Out</em> hosts
              founders, operators, athletes, policymakers, and outliers for
              the kind of unhurried conversations that don't survive on
              television. Episodes routinely cross millions of views and have
              helped move public discourse in India on money, health,
              ambition, and the practical realities of building something
              that lasts.
            </p>
          </div>
        </div>
      </section>

      {/* Keyword-focused profile sections */}
      <section className="border-y border-border bg-noir/30" aria-label="Raj Shamani profile">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Profile</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
              Raj Shamani — age, net worth, biography & podcast
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Quick answers to the most searched questions about Raj Shamani, with links to deeper pages.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
            {/* Age */}
            <div className="bg-background p-10">
              <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3">Age & Background</div>
              <h3 className="text-2xl font-display font-semibold mb-4">
                How old is Raj Shamani?
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                <Link to="/about" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Raj Shamani</Link> was born on 22 March 1996 in Indore, Madhya Pradesh, India. He grew up inside a Marwari business family running <Link to="/ventures" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Shamani Industries</Link>, and started working on the factory floor as a teenager. Today he is one of India's most followed business voices.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                <Link to="/about" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">Read the full biography <ArrowRight size={14} /></Link>
                <Link to="/faq" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">Raj Shamani FAQ <ArrowRight size={14} /></Link>
              </div>
            </div>

            {/* Net worth */}
            <div className="bg-background p-10">
              <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3">Net Worth & Investments</div>
              <h3 className="text-2xl font-display font-semibold mb-4">
                Raj Shamani net worth and portfolio
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Raj Shamani has built wealth across manufacturing, venture building, and early-stage investing. He co-founded <Link to="/ventures" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">House of X</Link>, holds 20+ angel investments across D2C and fintech, and is associated with Shamani Industries. Exact net worth figures vary by source — see the FAQ for a detailed breakdown.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                <Link to="/ventures" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">Ventures &amp; investments <ArrowRight size={14} /></Link>
                <Link to="/faq" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">Net worth details <ArrowRight size={14} /></Link>
              </div>
            </div>

            {/* Biography */}
            <div className="bg-background p-10">
              <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3">Biography & Education</div>
              <h3 className="text-2xl font-display font-semibold mb-4">
                Raj Shamani biography, education &amp; family
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                From selling on the factory floor in Indore to speaking at the United Nations, Raj Shamani's journey spans manufacturing, content, investing, and publishing. He studied at Daly College, Indore, and is the author of <Link to="/build-dont-talk" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Build, Don't Talk</Link> and host of <Link to="/podcast" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Figuring Out</Link>.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                <Link to="/about" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">Career milestones <ArrowRight size={14} /></Link>
                <Link to="/failure-resume" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">The failure resume <ArrowRight size={14} /></Link>
              </div>
            </div>

            {/* Podcast */}
            <div className="bg-background p-10">
              <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3">The Podcast</div>
              <h3 className="text-2xl font-display font-semibold mb-4">
                Figuring Out with Raj Shamani
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                <Link to="/podcast" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Figuring Out</Link> is India's most-watched long-form business podcast. Raj interviews founders, investors, athletes, and policymakers about building companies, making decisions, and navigating life. Watch on YouTube or listen on Spotify and Apple Podcasts.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                <Link to="/podcast" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">All podcast episodes <ArrowRight size={14} /></Link>
                <Link to="/guest" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80">Be a guest <ArrowRight size={14} /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quotes — targets "raj shamani quotes" */}
      <section className="border-y border-border bg-noir/20">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Quotes</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
              Raj Shamani quotes on building, money and leverage
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              The lines most shared from Raj Shamani's keynotes, podcasts and book — with the context behind each one.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                q: "Build, don't talk. The world rewards people who ship.",
                c: "On execution over conversation — the central idea of his book.",
              },
              {
                q: "Distribution is the only moat that lasts in the creator economy.",
                c: "On why audience-building matters as much as product-building.",
              },
              {
                q: "The best education is a business that forces you to solve real problems.",
                c: "On skipping the MBA and learning inside Shamani Industries.",
              },
            ].map((item) => (
              <div key={item.q} className="rounded-xl border border-border bg-card p-8">
                <Quote className="text-gold mb-5" size={22} strokeWidth={1.5} />
                <blockquote className="font-display text-lg font-semibold leading-snug mb-4">
                  {item.q}
                </blockquote>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.c}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/raj-shamani-quotes" className="inline-flex items-center gap-2 text-sm text-gold hover:opacity-80">
              Read the full collection of Raj Shamani quotes <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Shamani Industries — targets "shamani industries" */}
      <section className="border-y border-border bg-noir/30">
        <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Family Business</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
              Shamani Industries — where Raj Shamani learned business
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-muted-foreground leading-relaxed">
            <p>
              <strong className="text-foreground">Shamani Industries</strong> is the family manufacturing business based in Indore, Madhya Pradesh. It is where Raj Shamani started working on the factory floor as a teenager, learning sales, operations, and what it takes to run a real company.
            </p>
            <p>
              The business remains a core part of his portfolio alongside <Link to="/ventures" className="text-foreground underline underline-offset-4 decoration-gold/40 hover:decoration-gold">House of X</Link>, the Figuring Out media company, and his angel investments. Raj often credits Shamani Industries with teaching him the operator's discipline that now shapes how he evaluates founders and ventures.
            </p>
            <div className="pt-2">
              <Link to="/shamani-industries" className="inline-flex items-center gap-2 text-sm text-gold hover:opacity-80">
                More about Shamani Industries <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">What you'll find here</div>
        <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mb-12 max-w-3xl">
          A single home for the work, the investments, and the show.
        </h2>
        <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
          {[
            {
              t: "Long-form episodes worth your time",
              d: "Every Figuring Out conversation in one place — searchable, organised by guest, with the context behind why each story matters. Watch on YouTube or listen on Spotify and Apple Podcasts.",
            },
            {
              t: "An investing thesis you can actually read",
              d: "The portfolio companies, the categories Raj backs, and the patterns he looks for in founders. Useful whether you're raising, allocating, or just trying to understand where consumer India is heading.",
            },
            {
              t: "Talks, books, and ideas",
              d: "Keynotes, the bestselling book Build, Don't Talk, and the frameworks Raj returns to when teaching execution, distribution, and decision-making to the next generation of Indian builders.",
            },
            {
              t: "A way to actually reach the team",
              d: "Pitch yourself as a guest, request a speaking engagement, or send a serious business enquiry. Every submission is read by a real person — and the good ones get answered quickly.",
            },
          ].map((c) => (
            <div key={c.t} className="bg-background p-10">
              <h3 className="text-xl font-display font-semibold mb-3">{c.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Episodes */}
      <section className="border-y border-border bg-noir/20">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Figuring Out — Featured Episodes</div>
              <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight max-w-2xl">
                Open a full episode — summary, takeaways, and transcript.
              </h2>
            </div>
            <Link
              to="/podcast"
              className="inline-flex items-center gap-2 text-sm text-gold hover:opacity-80"
            >
              All episodes <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {episodes.slice(0, 6).map((ep) => (
              <Link
                key={ep.slug}
                to="/podcast/$slug"
                params={{ slug: ep.slug }}
                className="group p-6 rounded-xl bg-card border border-border hover:border-gold/40 transition flex gap-4 items-start"
              >
                <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:text-primary-foreground transition shrink-0">
                  <Play size={18} fill="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] tracking-widest uppercase text-gold mb-1">{ep.n} · {ep.d}</div>
                  <h3 className="font-bold leading-tight mb-1 line-clamp-2">{ep.t}</h3>
                  <p className="text-sm text-muted-foreground">with {ep.g}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>



      {/* The Book */}
      <section className="border-y border-border bg-noir/30">
        <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">The Book</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
              Build Don't Talk — Raj Shamani's bestselling book.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl leading-relaxed">
              A practical, India-first playbook on money, communication and
              building leverage in your twenties and thirties. Read the
              summary, key takeaways, and where to grab a copy.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/build-dont-talk"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
              >
                Read about the book
                <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
              </Link>
              <a
                href="https://www.amazon.in/Build-Dont-Talk-Things-Taught/dp/0143459686"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border text-foreground hover:border-gold/60 hover:text-gold transition"
              >
                Buy on Amazon
              </a>
              <a
                href="https://www.flipkart.com/build-don-t-talk/p/itm81394118c089f"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border text-foreground hover:border-gold/60 hover:text-gold transition"
              >
                Buy on Flipkart
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="p-10 rounded-sm border border-border bg-background">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Bestseller</div>
              <div className="font-display text-2xl font-semibold leading-snug">
                "Things you should have been taught in school, but weren't."
              </div>
              <div className="mt-6 text-sm text-muted-foreground">
                Published by Penguin · Available in paperback and Kindle.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Aion */}
      <section className="border-b border-border bg-noir/20">
        <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Aion</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
              Aion — Raj Shamani's AI course for builders.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl leading-relaxed">
              A hands-on programme on using AI to build, ship and sell faster —
              practical workflows, tools and prompts, made for Indian founders,
              creators and professionals. Starting at ₹11.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/aion"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
              >
                Explore Aion
                <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
              </Link>
              <a
                href="https://aion.engineer"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border text-foreground hover:border-gold/60 hover:text-gold transition"
              >
                Visit aion.engineer
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="p-10 rounded-sm border border-border bg-background">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">AI Course</div>
              <div className="font-display text-2xl font-semibold leading-snug">
                "Learn to build with AI — not just talk about it."
              </div>
              <div className="mt-6 text-sm text-muted-foreground">
                Live sessions · Real workflows · Beginner friendly.
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Be a Guest</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
              Have a story worth telling on Figuring Out?
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl">
              We're selective. Pitches are reviewed by the editorial team — the
              best ones move quickly. If you've built, broken, or rebuilt
              something real, we want to hear from you.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link
              to="/guest"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90"
            >
              Submit a Pitch <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ — targets brand and "people also ask" queries */}
      <section className="border-t border-border bg-noir/20">
        <div className="mx-auto max-w-4xl px-6 py-24">
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Frequently Asked</div>
          <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mb-12">
            About Raj Shamani
          </h2>
          <div className="divide-y divide-border border-y border-border">
            {homeFaq.map((f) => (

              <details key={f.q} className="group py-6">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">
                    {f.q}
                  </h3>
                  <span className="text-gold text-2xl leading-none mt-1 group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-sm text-muted-foreground">
            More answers on the{" "}
            <Link to="/faq" className="text-gold underline underline-offset-4">full Raj Shamani FAQ</Link>{" "}
            — including age, date of birth, net worth, education, family, and how to contact the team. Or jump to the{" "}
            <Link to="/about" className="text-gold underline underline-offset-4">biography</Link>,{" "}
            <Link to="/podcast" className="text-gold underline underline-offset-4">Figuring Out podcast</Link>,{" "}
            <Link to="/ventures" className="text-gold underline underline-offset-4">ventures</Link>, or{" "}
            <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">Build, Don't Talk</Link>.
          </div>
        </div>
      </section>

      <AionCta />
      <KeywordHubLinks />



      </div>
    </div>
  );
}
